import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProgramStates from '../components/superuser/ProgramStates';
import logo from '../assets/sgokit.svg';

/**
 * The SGOKit platform (superuser) console, modeled on grant_management's
 * SuperuserDashboard: a sidebar of tabs framing a work area, gated to system
 * admins. Rendered in SGOKit's own identity (sgokit.css `.sgk-admin`) rather than
 * grant_management's Tailwind.
 *
 * System admins have no organization, so this is a platform-domain surface: its
 * one tab today, Program states, edits the platform-wide opt-in roster. The tab
 * shell is the seam for the state-level program config still to come.
 */
type TabId = 'program-states';

const TABS: { id: TabId; name: string; description: string }[] = [
  {
    id: 'program-states',
    name: 'Program states',
    description: 'Which states have opted into the program',
  },
];

const SuperuserDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('program-states');

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'program-states':
        return <ProgramStates />;
      default:
        return null;
    }
  };

  return (
    <div className="sgk-site">
      <div className="sgk-admin">
        <aside className="sgk-admin__sidebar">
          <div className="sgk-admin__brand">
            <img className="sgk-brand__logo" src={logo} alt="" />
            <span className="sgk-brand__name">SGO<b>Kit</b></span>
          </div>

          <div className="sgk-admin__org">
            <div className="sgk-admin__org-label">Platform</div>
            <div className="sgk-admin__org-name" data-testid="superuser-scope-name">
              System administration
            </div>
          </div>

          <nav className="sgk-admin__nav" data-testid="superuser-sidebar-nav">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`sgk-admin__navlink${activeTab === tab.id ? ' is-active' : ''}`}
                data-testid={`superuser-${tab.id}-tab`}
              >
                {tab.name}
              </button>
            ))}
          </nav>

          <div className="sgk-admin__foot">
            <div className="sgk-admin__user" data-testid="superuser-user-email">
              {user?.email}
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="sgk-admin__signout"
              data-testid="superuser-logout-button"
            >
              Sign out
            </button>
          </div>
        </aside>

        <main className="sgk-admin__main">
          <div className="sgk-admin__inner" data-testid="superuser-tab-content">
            {renderTab()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperuserDashboard;
